import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs } from './utils'

/**
 * Divides the first provided number by the second provided number
 *
 * If the divisor is 0, the result is always null (i.e. X / 0 = null)
 *
 * @param expressions An expression producing a dividend followed by an
 * expression producing a divisor
 * @example
 * // Valid numbers
 * { '/': [ 6, 2 ] }
 * // ->
 * 3
 * // Division by zero
 * { '/': [ 6, 0 ] }
 * // ->
 * null
 * // Zero divided by a number
 * { '/': [ 0, 6 ] }
 * // ->
 * 0
 */
export interface Divide<out T = any> {
  /** {@link Divide} */
  readonly [Key.Divide]: T extends number
    ? readonly [dividend: Expression<number>, divisor: Expression<number>]
    : never
}
export namespace Divide {
  export const isDivide: TypeChecker<Divide> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Divide>({
      [Key.Divide]: CommonArgs.isTwoExpressions as TypeChecker<
        Divide[Key.Divide]
      >,
    }),
  ).usingAlias('Fn.Divide')
  export const assertIsDivide: TypeAsserter<Divide> =
    TypeAsserter.Factory.from(isDivide)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Divide,
    {
      readonly dividend: expressionNode.Node
      readonly divisor: expressionNode.Node
    }
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        [this.arg.dividend, this.arg.divisor].find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Divide, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Divide> = {
    createNode: (fn, context) => {
      const [dividendExpression, divisorExpression] = fn[Key.Divide]
      const dividend = expressionNode.from(dividendExpression, context)
      const divisor = expressionNode.from(divisorExpression, context)
      // NOTE: 0 / X is not simplified to 0 unless X is a value node, since X
      // may evaluate to 0, which makes the result null
      if (expressionNode.ForValue.isForValue(divisor)) {
        const divisorValue = Number(divisor.value)
        if (divisorValue === 0) {
          // If the divisor is 0, the result is always null (i.e. X / 0 = null)
          return new expressionNode.ForValue(null)
        }
        if (expressionNode.ForValue.isForValue(dividend)) {
          // If dividend and divisors are both values, we can calculate the
          // result
          return new expressionNode.ForValue(
            Number(dividend.value) / divisorValue,
          )
        }
        if (divisorValue === 1) {
          // If the divisor is 1, the result is just the dividend (i.e. X / 1 = X)
          return dividend
        }
      }
      // TODO: if dividend and divisor are effectively the same expression, we
      // can return a value of 1 (i.e. X / X = 1), as long as X is not 0
      return new Node({ dividend, divisor })
    },
    expressionsInArgs: fn => fn[Key.Divide],
    typeChecker: isDivide,
  }
}
